import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';
import { Close, CalendarMonth, Schedule, Insights } from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
}

const updates = [
  {
    icon: <CalendarMonth />,
    title: '休日設定の月またぎを修正',
    body: '先月の休みが今月の目標計算に混ざらないようにしました。ホームと設定画面は、今月・表示中の月を基準に休日を集計します。'
  },
  {
    icon: <Schedule />,
    title: 'シンプルモードを追加',
    body: '売上管理を中心に使いたい方向けに、時給や稼働時間まわりを減らした表示へ切り替えられるようにしました。記録画面もシンプルモード専用になります。'
  },
  {
    icon: <Insights />,
    title: '分析ページを見やすく改善',
    body: '売上・利益・時給・件数・サービス別の表示を切り替えながら確認できます。期間ごとの変化も、よりわかりやすい表現にしました。'
  }
];

function UpdateAnnouncementDialog({ open, onClose }: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#0f1512',
          color: 'white',
          borderRadius: '20px',
          border: '1px solid rgba(16,185,129,0.30)',
          overflow: 'hidden',
          m: { xs: 1.5, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 96px)', sm: 'calc(100dvh - 64px)' }
        }
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.70)',
          backdropFilter: 'blur(5px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'relative',
            height: { xs: 'min(620px, calc(100dvh - 96px))', sm: 500 },
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #0f1512 0%, #10241b 54%, #161307 100%)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="アップデート情報を閉じる"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#c5cec9',
              zIndex: 4,
              bgcolor: 'rgba(0,0,0,0.28)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.44)' }
            }}
          >
            <Close />
          </IconButton>

          <Box
            component="img"
            src="/assets/announcement-character-transparent.png"
            alt=""
            sx={{
              position: 'absolute',
              right: { xs: -58, sm: -62 },
              bottom: { xs: 54, sm: -42 },
              width: { xs: 190, sm: 460 },
              height: { xs: 260, sm: 590 },
              objectFit: 'contain',
              objectPosition: 'bottom center',
              filter: 'drop-shadow(0 22px 34px rgba(0,0,0,0.48))',
              opacity: { xs: 0.52, sm: 1 },
              pointerEvents: 'none',
              zIndex: 1
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: { xs: 'calc(100% - 56px)', sm: '66%' },
              px: { xs: 2, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 1.25, sm: 2 },
              flexShrink: 0
            }}
          >
            <Typography fontSize={13} fontWeight={800} color="#FCD34D" sx={{ mb: 0.75 }}>
              UPDATE
            </Typography>
            <Typography fontSize={{ xs: 25, sm: 30 }} fontWeight={900} lineHeight={1.15}>
              最新アップデートのお知らせ
            </Typography>
            <Typography fontSize={15} color="#b8c3bd" lineHeight={1.65} sx={{ mt: 1.25 }}>
              今回の改善内容をまとめました。使いやすくなった部分をサッと確認できます。
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              mx: { xs: 2, sm: 3 },
              width: { xs: 'auto', sm: 372 },
              maxWidth: { sm: 'calc(100% - 180px)' },
              zIndex: 3,
              p: { xs: 1.5, sm: 2 },
              borderRadius: '18px',
              bgcolor: 'rgba(10,15,12,0.88)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 16px 36px rgba(0,0,0,0.34)',
              backdropFilter: 'blur(8px)',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              mb: 1,
              pr: { xs: 1, sm: 2 },
              '&::after': {
                display: { xs: 'none', sm: 'block' },
                content: '""',
                position: 'absolute',
                right: -13,
                bottom: 96,
                width: 26,
                height: 26,
                bgcolor: 'rgba(10,15,12,0.88)',
                borderRight: '1px solid rgba(255,255,255,0.10)',
                borderBottom: '1px solid rgba(255,255,255,0.10)',
                transform: 'rotate(-45deg)'
              }
            }}
          >
            {updates.map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  display: 'flex',
                  gap: { xs: 1, sm: 1.2 },
                  pb: index === updates.length - 1 ? 0 : { xs: 1.1, sm: 1.4 },
                  mb: index === updates.length - 1 ? 0 : { xs: 1.1, sm: 1.4 },
                  borderBottom: index === updates.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <Box
                  sx={{
                    width: { xs: 34, sm: 36 },
                    height: { xs: 34, sm: 36 },
                    borderRadius: '12px',
                    bgcolor: 'rgba(16,185,129,0.16)',
                    color: '#34D399',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    '& svg': { fontSize: 21 }
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={{ xs: 14.5, sm: 15 }} fontWeight={800} color="white" lineHeight={1.35}>
                    {item.title}
                  </Typography>
                  <Typography fontSize={{ xs: 12.5, sm: 13 }} color="#b8c3bd" lineHeight={1.5} sx={{ mt: 0.35 }}>
                    {item.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              position: 'relative',
              alignSelf: 'flex-start',
              ml: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 2.5 },
              zIndex: 3,
              height: 46,
              px: 3,
              borderRadius: '999px',
              bgcolor: '#10B981',
              color: '#06110c',
              '&:hover': { bgcolor: '#34D399' }
            }}
          >
            確認しました
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateAnnouncementDialog;
